import { CommandKind, TimedCommand } from "../sim/command";
import { SimEventKind } from "../sim/events";
import { LevelPhase, LevelSim } from "../sim/levelSim";
import { OrderCounts } from "./orders";
import { PlayerProfile, UpgradeTarget } from "./profile";

/**
 * What a battle did, in Daily Orders keys (orders.json `metric`). The host shows it each tick's events before the sim
 * clears them (`observe`), and the command log at the end (`finish`); it reads the sim, never changes it.
 */
export class BattleTally {
  private readonly counts: Record<string, number> = {};
  /** Tower archetype ids placed this battle (non-base), for "win with a <tower>". */
  private readonly towersUsed: string[] = [];

  private add(key: string, n = 1): void { this.counts[key] = (this.counts[key] ?? 0) + n; }

  /** Counts the events in the sim's buffer now (call once per tick, before the buffer is cleared). */
  observe(sim: LevelSim): void {
    const B = sim.balance;
    for (const ev of sim.events.all()) {
      switch (ev.kind) {
        case SimEventKind.EnemyKilled: {
          this.add("kills:any");
          // the dead enemy's slot is still intact until the next tick's spawns reuse it
          const e = sim.enemySlots.find((x) => x.id === ev.a);
          if (!e) break;
          const a = B.enemies[e.archetype];
          this.add(`kills:${a.id}`);
          if (a.isAir) this.add("kills:air");
          // elites by affix (lifetime stats, meta/stats.ts); no order counts these keys
          if (e.affix >= 0) this.add(`kills:elite:${B.affixes[e.affix].id}`);
          break;
        }
        case SimEventKind.TowerPlaced: {
          const t = sim.findTower(ev.a);
          if (!t || B.towers[t.archetype].isBase) break;
          this.add("towersPlaced");
          const id = B.towers[t.archetype].id;
          if (!this.towersUsed.includes(id)) this.towersUsed.push(id);
          break;
        }
        case SimEventKind.TowerUpgraded: this.add("towerUpgrades"); break;
        case SimEventKind.SegmentPlaced: this.add("roadCards"); break;
        case SimEventKind.StageCleared: this.add("stagesCleared"); break;
        default: break;
      }
    }
  }

  /**
   * The totals so far without changing the tally: what `observe` would add from the sim's buffer now, then `finish`. The
   * end screen reads it (MP-21: an event's kills, elites and early calls) before the level is left and the tally closed.
   */
  peek(sim: LevelSim, commands: readonly TimedCommand[]): OrderCounts {
    const copy = new BattleTally();
    Object.assign(copy.counts, this.counts);
    copy.towersUsed.push(...this.towersUsed);
    copy.observe(sim);
    return copy.finish(sim, commands);
  }

  /** The battle's totals: the events seen plus what only the end state and the command log say (wins, early calls). */
  finish(sim: LevelSim, commands: readonly TimedCommand[]): OrderCounts {
    const out: Record<string, number> = { ...this.counts };
    const called = commands.filter((c) => c.command.kind === CommandKind.CallNextStage).length;
    if (called > 0) out.wavesCalled = called;
    if (sim.phase === LevelPhase.Won) {
      out["wins:any"] = 1;
      if (sim.leaks === 0) out["wins:noLeak"] = 1;
      for (const id of this.towersUsed) out[`wins:${id}`] = 1;
    }
    return out;
  }
}

/** The parts of the profile the hub orders watch, taken before an action so the next save can tell what it did. */
export interface HubSnapshot {
  readonly jobs: readonly { readonly id: number; readonly target: UpgradeTarget; readonly towerIndex: number; readonly toLevel: number }[];
  readonly gear: readonly { readonly id: number; readonly owner: string; readonly raised: number }[];
  readonly speedUpMinutes: number;
}

const ownerKey = (p: PlayerProfile, id: number): string => {
  const g = p.findGear(id)?.equippedTo;
  return g ? `${g.target}:${g.towerIndex}` : "";
};

export function hubSnapshot(p: PlayerProfile): HubSnapshot {
  return {
    jobs: p.jobs.map((j) => ({ id: j.id, target: j.target, towerIndex: j.towerIndex, toLevel: j.toLevel })),
    gear: p.gear.map((g) => ({ id: g.id, owner: ownerKey(p, g.id), raised: g.raised })),
    speedUpMinutes: p.speedUps.reduce((s, m) => s + m, 0),
  };
}

/**
 * What the player did in the lobby between a snapshot and now, in order keys: upgrades started and collected, gear
 * equipped, upgraded and salvaged, a speed-up used. The hub saves after every action, so one save sees one action.
 */
export function hubProgress(before: HubSnapshot, p: PlayerProfile): OrderCounts {
  const out: Record<string, number> = {};
  const add = (k: string, n: number) => { if (n > 0) out[k] = (out[k] ?? 0) + n; };
  add("hubUpgrades", p.jobs.filter((j) => !before.jobs.some((b) => b.id === j.id)).length);
  add("upgradesCollected", before.jobs.filter((b) => !p.jobs.some((j) => j.id === b.id) && p.levelOf(b.target, b.towerIndex) >= b.toLevel).length);
  for (const b of before.gear) {
    const now = p.findGear(b.id);
    if (!now) { add("gearSalvaged", 1); continue; }
    const owner = ownerKey(p, b.id);
    if (owner !== "" && owner !== b.owner) add("gearEquipped", 1);
    add("gearUpgraded", now.raised - b.raised);
  }
  if (p.speedUps.reduce((s, m) => s + m, 0) < before.speedUpMinutes) add("speedUpsUsed", 1);
  return out;
}
